import { LegalPageLayout } from "./legal/LegalPageLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Mail, MapPin, Phone, Send } from "lucide-react";
import { SEO } from "@/components/SEO";
import { useState } from "react";
import { toast } from "sonner";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: ""
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Construire le lien mailto avec les données du formulaire
    const mailtoLink = `mailto:contact@libraddict.fr?subject=${encodeURIComponent(formData.subject)}&body=${encodeURIComponent(
      `Nom: ${formData.name}\nEmail: ${formData.email}\n\nMessage:\n${formData.message}`
    )}`;
    
    window.location.href = mailtoLink;
    
    toast.success("Votre client email va s'ouvrir pour envoyer le message");
    
    // Réinitialiser le formulaire
    setFormData({
      name: "",
      email: "",
      subject: "",
      message: ""
    });
  };

  return (
    <>
      <SEO 
        title="Contact — Nous Contacter"
        description="Contactez l'équipe Libr'Addict pour toute question, suggestion ou partenariat. Nous sommes à votre écoute."
      />
      <LegalPageLayout title="Nous Contacter">
        <div className="space-y-8 animate-fade-in">
          {/* Introduction */}
          <Card className="border border-primary/20 bg-card">
            <CardHeader>
              <CardTitle className="text-2xl md:text-3xl">Restons en Contact</CardTitle>
              <CardDescription className="text-lg mt-2">
                Une question, une suggestion, un partenariat ? Nous serions ravis d'échanger avec vous
              </CardDescription>
            </CardHeader>
          </Card>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Contact Form */}
            <div className="md:col-span-2">
              <Card className="glass-card">
                <CardHeader>
                  <CardTitle className="text-xl">Envoyez-nous un Message</CardTitle>
                  <CardDescription>
                    Remplissez le formulaire ci-dessous et nous vous répondrons dans les plus brefs délais
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="name">Nom complet *</Label>
                        <Input
                          id="name"
                          value={formData.name}
                          onChange={(e) => setFormData({...formData, name: e.target.value})}
                          placeholder="Votre nom"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">Email *</Label>
                        <Input
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({...formData, email: e.target.value})}
                          placeholder="votre@email.com"
                          required
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="subject">Sujet *</Label>
                      <Input
                        id="subject"
                        value={formData.subject}
                        onChange={(e) => setFormData({...formData, subject: e.target.value})}
                        placeholder="L'objet de votre message"
                        required
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="message">Message *</Label>
                      <Textarea
                        id="message"
                        value={formData.message}
                        onChange={(e) => setFormData({...formData, message: e.target.value})}
                        placeholder="Écrivez votre message ici..."
                        rows={6}
                        required
                      />
                    </div>

                    <Button type="submit" className="w-full shadow-sm">
                      <Send className="w-4 h-4 mr-2" />
                      Envoyer le Message
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Contact Info */}
            <div className="space-y-6">
              <Card className="hover-scale">
                <CardHeader>
                  <div className="flex items-center gap-3 mb-2">
                    <Mail className="w-6 h-6 text-primary" />
                    <CardTitle className="text-lg">Email</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <a 
                    href="mailto:contact@libraddict.fr" 
                    className="text-primary hover:underline font-medium"
                  >
                    contact@libraddict.fr
                  </a>
                  <p className="text-sm text-muted-foreground mt-2">
                    Réponse sous 24-48h
                  </p>
                </CardContent>
              </Card>

              <Card className="hover-scale">
                <CardHeader>
                  <div className="flex items-center gap-3 mb-2">
                    <Phone className="w-6 h-6 text-accent" />
                    <CardTitle className="text-lg">Téléphone</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="font-medium text-muted-foreground">
                    Bientôt disponible
                  </p>
                  <p className="text-sm text-muted-foreground mt-2">
                    Lun-Ven : 9h-18h
                  </p>
                </CardContent>
              </Card>

              <Card className="hover-scale">
                <CardHeader>
                  <div className="flex items-center gap-3 mb-2">
                    <MapPin className="w-6 h-6 text-primary" />
                    <CardTitle className="text-lg">Localisation</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Paris, France
                  </p>
                  <p className="text-sm text-muted-foreground mt-2">
                    Service 100% en ligne
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Partnerships */}
          <Card className="border border-accent/30 bg-card">
            <CardHeader>
              <CardTitle className="text-2xl">Partenariats et Collaborations</CardTitle>
              <CardDescription className="text-base">
                Vous représentez une institution, une association ou vous souhaitez collaborer avec Libr'Addict ?
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground">
                Nous sommes ouverts aux partenariats avec :
              </p>
              <ul className="space-y-2 text-muted-foreground ml-4">
                <li>• Centres de soins en addictologie (CSAPA, CAARUD)</li>
                <li>• Établissements de santé et hôpitaux</li>
                <li>• Associations de prévention et de réduction des risques</li>
                <li>• Organismes de formation en addictologie</li>
                <li>• Collectivités territoriales</li>
              </ul>
              <Button 
                variant="outline"
                onClick={() => window.location.href = 'mailto:contact@libraddict.fr?subject=Demande de Partenariat'}
              >
                <Mail className="w-4 h-4 mr-2" />
                Proposer un Partenariat
              </Button>
            </CardContent>
          </Card>
        </div>
      </LegalPageLayout>
    </>
  );
};

export default Contact;
